'use client';

import { lazy, Suspense, useEffect, useState } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import type { Guide, GuideEvent } from './GuidedWalkthrough';

/**
 * LIGHTWEIGHT global auto-starter for guided walkthroughs.
 *
 * Mounted ONCE in the root layout. On every page it does nothing until the URL carries a
 * `?guide=<id>` param — that's the deep-link the SOP handbook's "Open live & run the
 * walkthrough" button produces (`${doc.route}?guide=${doc.id}`). Only THEN does it:
 *   1. lazily fetch that ONE guide's spec from GET /api/guides/[id] (so all of GUIDES is
 *      NEVER imported into the global/every-page client bundle — no bloat, no data re-leak),
 *   2. lazily code-split + mount the GuidedWalkthrough engine on the live page,
 *   3. clear the `?guide` param when the tour completes/exits (so a refresh doesn't restart
 *      it and the URL stays clean/shareable-to-the-page).
 *
 * The engine already tolerates missing/late anchors (it holds, self-heals, and shows
 * "waiting…"), so landing on the real page before it finishes rendering is fine.
 */

const GuidedWalkthrough = lazy(() => import('./GuidedWalkthrough'));

// Fire-and-forget analytics — matches the handbook's walkthrough logging so deep-linked runs are measured too.
function logGuideEvent(e: GuideEvent) {
  try {
    const body = JSON.stringify(e);
    if (navigator.sendBeacon) navigator.sendBeacon('/api/guides/event', body);
    else fetch('/api/guides/event', { method: 'POST', body, keepalive: true }).catch(() => {});
  } catch {
    /* analytics must never break the guide */
  }
}

export default function GuideAutoStart() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const guideId = searchParams.get('guide');
  const [guide, setGuide] = useState<Guide | null>(null);

  // Remove ?guide (and any leftover keys) from the URL without a navigation, so the tour
  // doesn't relaunch on refresh and the address bar reads cleanly.
  const clearParam = () => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    url.searchParams.delete('guide');
    window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
  };

  // Load the single guide spec on demand whenever ?guide changes to a new id.
  useEffect(() => {
    if (!guideId) {
      setGuide(null);
      return;
    }
    let cancelled = false;
    setGuide(null);
    fetch(`/api/guides/${encodeURIComponent(guideId)}`, { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((g: Guide | null) => {
        if (cancelled) return;
        if (g && Array.isArray(g.steps) && g.steps.length > 0) setGuide(g);
        else clearParam(); // unknown id / no runnable steps — don't leave a dead param
      })
      .catch(() => {
        if (!cancelled) clearParam();
      });
    return () => {
      cancelled = true;
    };
    // pathname included so a same-id link on a DIFFERENT page still re-triggers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guideId, pathname]);

  if (!guide) return null;

  const finish = () => {
    setGuide(null);
    clearParam();
  };

  return (
    <Suspense fallback={null}>
      <GuidedWalkthrough data-component-id="src/components/guides/GuideAutoStart.tsx:85:6" data-component-path="src/components/guides/GuideAutoStart.tsx" data-component-line="85" data-component-end-line="85" data-component-file="GuideAutoStart.tsx" data-component-name="GuidedWalkthrough" data-component-content="%7B%22elementName%22%3A%22GuidedWalkthrough%22%7D" guide={guide} onExit={finish} onEvent={logGuideEvent} />
    </Suspense>
  );
}
